// Baut das vollständige HTML-Dokument, das in den Preview-Iframe geht.
// Struktur: <head> mit Stylesheet → <body> mit Cover → Title → ToC → Content.
//
// Paged.js wird im Iframe nachgeladen (CDN), nicht hier — siehe pagedjsPreview.ts.
import { buildStylesheet } from "./stylesheetBuilder";
import { buildTocHtml } from "./tocBuilder";
/**
 * Markiert den ersten `<p>` nach jedem `<h1>` mit `class="dropcap-target"`.
 * Das funktioniert auch wenn zwischen H1 und dem ersten Absatz noch H2/H3
 * stehen — was Type.ai-MD oft tut. CSS-Adjacent-Selector wäre zu fragil.
 *
 * String-basiert, weil wir das ohne echtes DOM-Parsing im Frontend machen
 * können (kein DOMParser → langsam für 200-Seiten-Bücher).
 */
function wrapChapters(bodyHtml) {
    // Sammle die Indizes, an denen der erste <p…> nach jedem <h1> startet.
    const insertions = [];
    const h1Re = /<h1[\s>]/g;
    let m;
    while ((m = h1Re.exec(bodyHtml)) !== null) {
        // Erste <p ohne dazwischenliegendes <h1>
        const rest = bodyHtml.slice(m.index + 3);
        const pRe = /<p(\s|>)/;
        const pHit = rest.match(pRe);
        if (!pHit)
            continue;
        const pStart = m.index + 3 + (pHit.index ?? 0);
        const before = bodyHtml.slice(m.index + 1, pStart);
        if (/<h1[\s>]/.test(before))
            continue; // anderes H1 dazwischen → skip
        insertions.push(pStart);
    }
    // Von hinten nach vorne ersetzen damit Indizes stabil bleiben.
    insertions.sort((a, b) => b - a);
    let out = bodyHtml;
    for (const idx of insertions) {
        // "<p" → "<p class=\"dropcap-target\""
        out = out.slice(0, idx) + '<p class="dropcap-target"' + out.slice(idx + 2);
    }
    return out;
}
/**
 * Unsichtbare Anker-Elemente am Body-Anfang, die Buchtitel und Autor als
 * benannte CSS-Strings (`book-title`, `author`) setzen. Diese füllen die
 * Running-Header-Variablen {book_title}/{author}. Sie stehen ganz vorne, damit
 * der String ab der ersten Seite verfügbar ist. Paged.js ignoriert `string-set`
 * auf dem <body> selbst — deshalb dieser Umweg über echte Flow-Elemente.
 */
function buildStringAnchors(s) {
    return (`<div class="book-title-anchor" aria-hidden="true">${escapeHtml(s.meta.title || "")}</div>` +
        `<div class="author-anchor" aria-hidden="true">${escapeHtml(s.meta.author || "")}</div>`);
}
function buildCoverHtml(s) {
    // U1 — Vorderseite. „Im PDF inkludieren" aus → Seite ganz weglassen.
    if (!s.cover.includeInPdf)
        return "";
    if (s.cover.mode === "image" && s.cover.imageDataUrl) {
        return `<section class="cover-page"><img src="${s.cover.imageDataUrl}" alt="Cover" /></section>`;
    }
    if (s.cover.mode === "template") {
        return `<section class="cover-page" style="background:${s.colors.accent}; color:#fff;">
      <div>
        <div style="font-family:${cssFont(s.headings.h1.fontFamily)}; font-size:${s.headings.h1.sizePt * 1.4}pt; font-weight:700; padding:0 2em;">${escapeHtml(s.meta.title)}</div>
        ${s.meta.subtitle ? `<div style="font-style:italic; margin-top:1em; font-size:${s.typography.bodySizePt * 1.2}pt;">${escapeHtml(s.meta.subtitle)}</div>` : ""}
        <div style="position:absolute; bottom:10%; left:0; right:0; font-size:${s.typography.bodySizePt}pt; letter-spacing:0.2em; text-transform:uppercase;">${escapeHtml(s.meta.author)}</div>
      </div>
    </section>`;
    }
    return "";
}
/**
 * Eine Umschlag-Wrapper-Seite (Innenseite vorne/hinten, Rückseite).
 * `include` aus → leerer String (Seite wird nicht erzeugt). Mit Bild → Bildseite,
 * sonst leere Platzhalterseite (reserviert die physische Seite).
 */
function buildWrapperPageHtml(page, cssClass, alt) {
    if (!page?.include)
        return "";
    const img = page.imageDataUrl
        ? `<img src="${page.imageDataUrl}" alt="${alt}" />`
        : "";
    return `<section class="${cssClass}">${img}</section>`;
}
function buildTitlePageHtml(s) {
    return `<section class="title-page">
    <div class="book-title">${escapeHtml(s.meta.title)}</div>
    ${s.meta.subtitle ? `<div class="book-subtitle">${escapeHtml(s.meta.subtitle)}</div>` : ""}
    <div class="book-author">${escapeHtml(s.meta.author)}</div>
  </section>`;
}
/**
 * Impressum-/Copyright-Seite. Liegt zwischen Titelseite und Inhaltsverzeichnis
 * — im deutschen Buchsatz der klassische Ort (Rückseite der Titelseite). Inhalt
 * sitzt unten bündig (siehe @page impressum im Stylesheet); optionales Verlags-
 * logo über dem Freitext. Leerzeile → neuer Absatz, einzelner Umbruch → <br>.
 * Aus bzw. weder Logo noch Text → kein Markup (Seite wird gar nicht erzeugt).
 */
function buildImpressumHtml(s) {
    if (!s.impressum?.enabled)
        return "";
    const body = (s.impressum.body || "").trim();
    const logo = s.impressum.logoDataUrl
        ? `<img class="impressum-logo" src="${s.impressum.logoDataUrl}" alt="Verlagslogo" />`
        : "";
    if (!body && !logo)
        return "";
    const paragraphs = body
        ? body
            .split(/\n{2,}/)
            .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br />")}</p>`)
            .join("\n")
        : "";
    return `<section class="impressum-page">${logo}${paragraphs}</section>`;
}
/**
 * Ersetzt `src`-Attribute aller `<img>` im HTML, wenn sie in der Override-Map
 * stehen. Schaut sowohl auf den vollen src-Wert als auch auf den Basename — so
 * funktioniert es egal ob der MD-Pfad `bilder/foo.jpg` oder `./bilder/foo.jpg`
 * oder nur `foo.jpg` lautet.
 *
 * String-basiert (kein DOMParser) — gleicher Grund wie wrapChapters: schnell
 * genug für lange Bücher, und der HTML-Output kommt von rehype-stringify und
 * ist gut formatiert.
 */
function applyImageOverrides(html, overrides) {
    if (!html || Object.keys(overrides).length === 0)
        return html;
    // Basename-Lookup vorbereiten — fallback wenn der exakte Pfad nicht matched
    const byBasename = {};
    for (const [k, v] of Object.entries(overrides)) {
        const base = k.split(/[\\/]/).pop() || k;
        byBasename[base.toLowerCase()] = v;
    }
    return html.replace(/<img\b([^>]*?)\ssrc="([^"]+)"([^>]*)>/gi, (full, pre, src, post) => {
        if (src.startsWith("data:") || src.startsWith("http://") || src.startsWith("https://")) {
            return full; // schon eine vollwertige URL
        }
        const direct = overrides[src];
        if (direct)
            return `<img${pre} src="${direct}"${post}>`;
        const base = (src.split(/[\\/]/).pop() || src).toLowerCase();
        const byBase = byBasename[base];
        if (byBase)
            return `<img${pre} src="${byBase}"${post}>`;
        return full;
    });
}
export function buildBookDocument({ settings, bodyHtml, headings, imageOverrides = {} }) {
    const css = buildStylesheet(settings);
    const tocHtml = buildTocHtml(headings, settings);
    const patched = applyImageOverrides(bodyHtml, imageOverrides);
    const chapters = wrapChapters(patched);
    return `<!doctype html>
<html lang="${settings.meta.language}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(settings.meta.title)} — Preview</title>
<style>${css}</style>
</head>
<body>
${buildStringAnchors(settings)}
${buildCoverHtml(settings)}
${buildWrapperPageHtml(settings.cover.insideFront, "inside-front-page", "Cover-Innenseite vorne")}
${buildTitlePageHtml(settings)}
${buildImpressumHtml(settings)}
${tocHtml}
${chapters}
${buildWrapperPageHtml(settings.cover.insideBack, "inside-back-page", "Rückseite innen")}
${buildWrapperPageHtml(settings.cover.back, "back-cover-page", "Rückseite")}
</body>
</html>`;
}
function escapeHtml(s) {
    return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function cssFont(family) {
    const fallback = /sans/i.test(family) ? "sans-serif" : "serif";
    if (/^[A-Za-z0-9_-]+$/.test(family))
        return `${family}, ${fallback}`;
    return `"${family}", ${fallback}`;
}
